import { Booking, Resource } from '@/types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, User, ArrowRight } from 'lucide-react';

interface BookingDragConfirmDialogProps {
  open: boolean;
  booking: Booking | null;
  originalResource?: Resource;
  newResource?: Resource;
  originalStartTime: Date | null;
  originalEndTime: Date | null;
  newStartTime: Date | null;
  newEndTime: Date | null;
  onConfirm: () => void;
  onDiscard: () => void;
}

export function BookingDragConfirmDialog({
  open,
  booking,
  originalResource,
  newResource,
  originalStartTime,
  originalEndTime,
  newStartTime,
  newEndTime,
  onConfirm,
  onDiscard,
}: BookingDragConfirmDialogProps) {
  if (!booking || !originalStartTime || !originalEndTime || !newStartTime || !newEndTime) {
    return null;
  }

  const hasResourceChanged = String(originalResource?.id) !== String(newResource?.id);
  const hasTimeChanged = 
    originalStartTime.getTime() !== newStartTime.getTime() || 
    originalEndTime.getTime() !== newEndTime.getTime();

  // Determine if this is an hourly booking (has specific times, not full-day)
  // Half-day bookings also have specific times and use local dates, so treat them the same way
  const isHourlyBooking = booking.durationType === 'hourly' || 
    booking.durationType === 'half-day' ||
    booking.durationType === 'half-day-am' ||
    booking.durationType === 'half-day-pm' ||
    (!booking.durationType && (newStartTime.getHours() !== 0 || newStartTime.getMinutes() !== 0));

  // Format dates for display
  // For full-day bookings: use UTC methods since dates are created with Date.UTC()
  // For hourly bookings: use local methods since times are in local timezone
  const formatDate = (date: Date) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    if (isHourlyBooking) {
      // Local time for hourly bookings
      const month = months[date.getMonth()];
      const day = date.getDate();
      const year = date.getFullYear();
      return `${month} ${day}, ${year}`;
    } else {
      // UTC time for full-day bookings
      const month = months[date.getUTCMonth()];
      const day = date.getUTCDate();
      const year = date.getUTCFullYear();
      return `${month} ${day}, ${year}`;
    }
  };

  const formatDateTime = (date: Date) => {
    if (isHourlyBooking) {
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${formatDate(date)} ${hours}:${minutes}`;
    } else {
      const hours = date.getUTCHours().toString().padStart(2, '0');
      const minutes = date.getUTCMinutes().toString().padStart(2, '0');
      return `${formatDate(date)} ${hours}:${minutes}`;
    }
  };

  const formatTime = (date: Date) => {
    if (isHourlyBooking) {
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    } else {
      const hours = date.getUTCHours().toString().padStart(2, '0');
      const minutes = date.getUTCMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    }
  };

  const getDuration = (start: Date, end: Date) => {
    const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
    if (hours < 1) {
      return `${Math.round(hours * 60)}m`;
    }
    return `${hours.toFixed(1)}h`;
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onDiscard()}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="text-xl">Confirm Booking Changes</DialogTitle>
          <DialogDescription>
            Review the changes to this booking before confirming.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Booking Info */}
          <div className="rounded-lg border p-3 bg-muted/50 shadow-sm">
            <div className="font-semibold text-base mb-1">{booking.projectName}</div>
            {booking.description && (
              <div className="text-xs text-muted-foreground">{booking.description}</div>
            )}
          </div>

          {/* Resource Change */}
          {hasResourceChanged && originalResource && newResource && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <User className="h-4 w-4" />
                <span>Resource</span>
              </div>
              <div className="flex items-center gap-3 pl-6">
                <div className="flex-1 rounded-lg border p-3 bg-background shadow-sm">
                  <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">From</div>
                  <div className="text-sm font-semibold">{originalResource.name}</div>
                  <div className="text-xs text-muted-foreground">{originalResource.role}</div>
                </div>
                <ArrowRight className="h-5 w-5 text-primary flex-shrink-0" />
                <div className="flex-1 rounded-lg border-2 p-3 bg-primary/10 border-primary/40 shadow-sm">
                  <div className="text-xs text-primary uppercase tracking-wide mb-1 font-medium">To</div>
                  <div className="text-sm font-semibold text-primary">{newResource.name}</div>
                  <div className="text-xs text-muted-foreground">{newResource.role}</div>
                </div>
              </div>
            </div>
          )}

          {/* Time Change */}
          {hasTimeChanged && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Clock className="h-4 w-4" />
                <span>Time & Duration</span>
              </div>
              <div className="flex items-center gap-3 pl-6">
                <div className="flex-1 rounded-lg border p-3 bg-background shadow-sm">
                  <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">From</div>
                  <div className="text-sm font-semibold">{formatDate(originalStartTime)}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    to {formatDate(originalEndTime)} • {getDuration(originalStartTime, originalEndTime)}
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-primary flex-shrink-0" />
                <div className="flex-1 rounded-lg border-2 p-3 bg-primary/10 border-primary/40 shadow-sm">
                  <div className="text-xs text-primary uppercase tracking-wide mb-1 font-medium">To</div>
                  <div className="text-sm font-semibold text-primary">{formatDate(newStartTime)}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    to {formatDate(newEndTime)} • {getDuration(newStartTime, newEndTime)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Summary */}
          <div className="rounded-lg border p-3 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900">
            <div className="flex items-start gap-2">
              <Calendar className="h-4 w-4 mt-0.5 text-blue-600 dark:text-blue-400" />
              <div className="text-xs text-blue-900 dark:text-blue-100">
                {hasResourceChanged && hasTimeChanged && (
                  <span>This booking will be moved to a different resource and time.</span>
                )}
                {hasResourceChanged && !hasTimeChanged && (
                  <span>This booking will be moved to a different resource.</span>
                )}
                {!hasResourceChanged && hasTimeChanged && (
                  <span>This booking's time and duration will be changed.</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onDiscard} className="min-w-[120px]">
            Discard Changes
          </Button>
          <Button onClick={onConfirm} className="min-w-[120px]">
            Confirm Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
